import { useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { ExternalLink, ShoppingCart } from 'lucide-react'
import { api } from '@/api/client'
import type { PropertyCalendarSupply, PropertyCalendarSupplyDelivery } from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { CONFIDENCE_LABELS, CONFIDENCE_TONES } from '@/lib/supply-confidence'
import { describeDelivery, formatSupplyDay } from '@/lib/supply-events'
import { categoryLabel } from '@/pages/supplies-dashboard'
import { routes } from '@/lib/routes'
import { useRangeWords } from './range-words'

/**
 * What the calendar read says about the opened category. `pending` is "not read
 * yet"; an `error` layer is said out loud (an unreadable layer is not "no
 * supplies"); `ok` with no item means the month on screen has nothing for it.
 */
export type SupplyLookup =
  | { status: 'pending' }
  | { status: 'error'; message: string }
  | { status: 'ok'; supply: PropertyCalendarSupply | null }

interface SupplySheetProps {
  propertyId: string
  category: string
  /** The marker's day, `YYYY-MM-DD`: says which event the operator came from. */
  day: string
  supply: SupplyLookup
  onClose: () => void
}

/**
 * One supply category, opened from a runout or delivery marker on the property
 * calendar.
 *
 * Everything shown is the supply model's, as the calendar read returned it:
 * the last delivery, the stock estimate, the confidence and the model's own
 * reasoning sentence, which is not reworded or recomputed. The one action is
 * "What else to order", which is the supplies page's existing restock READ: it
 * lists other categories the model expects to run out soon, and nothing here
 * places, drafts or triggers an order.
 */
export function SupplySheet({ propertyId, category, day, supply, onClose }: SupplySheetProps) {
  const item = supply.status === 'ok' ? supply.supply : null
  const title = item?.label ?? categoryLabel(category)
  const words = useRangeWords()

  return (
    <Sheet open onOpenChange={open => !open && onClose()}>
      <SheetContent aria-describedby="supply-sheet-description" data-testid="supply-sheet">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription id="supply-sheet-description">
            {item ? occasion(item, day) : 'Supply'}
          </SheetDescription>
        </SheetHeader>

        <SheetBody className="space-y-5">
          {supply.status === 'pending' ? (
            <div className="space-y-3" aria-busy="true">
              <LoadingSkeleton className="h-6 w-2/3" />
              <LoadingSkeleton className="h-32 w-full" />
            </div>
          ) : supply.status === 'error' ? (
            <p className="text-sm text-danger" role="alert">
              {supply.message}
            </p>
          ) : !item ? (
            <p className="text-sm text-textMuted" data-testid="supply-sheet-missing">
              {title} has no runout or delivery on {formatSupplyDay(day, { withYear: true })} in{' '}
              {words.shown}. The supplies page has its full history.
            </p>
          ) : (
            <>
              <SupplyFacts supply={item} />
              {item.reasoning && (
                <section aria-label="Why" className="space-y-1.5">
                  <SectionHeading>Why</SectionHeading>
                  <p className="text-sm text-text" data-testid="supply-sheet-reasoning">
                    {item.reasoning}
                  </p>
                </section>
              )}
              <DeliveriesThisMonth deliveries={item.deliveries} />
              <RestockSection propertyId={propertyId} category={category} />
            </>
          )}
        </SheetBody>

        <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-border px-6 py-4">
          <Button variant="secondary" size="sm" asChild>
            <Link to={`${routes.supplies.path}?property=${encodeURIComponent(propertyId)}`}>
              Open supplies page
              <ExternalLink className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}

/** Which event on the calendar the operator opened this from, in words. */
function occasion(supply: PropertyCalendarSupply, day: string): string {
  const parts: string[] = []
  if (supply.runout_in_range && supply.runout_date === day) {
    parts.push(`Expected to run out ${formatSupplyDay(day, { withYear: true })}`)
  }
  if (supply.deliveries.some(delivery => delivery.date === day)) {
    parts.push(`Delivered ${formatSupplyDay(day, { withYear: true })}`)
  }
  return parts.length > 0 ? parts.join(' · ') : 'Supply'
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h3 className="text-xs font-semibold uppercase tracking-wider text-textFaint">{children}</h3>
  )
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2">
      <dt className="shrink-0 text-sm text-textMuted">{label}</dt>
      <dd className="min-w-0 text-right text-sm text-text">{children}</dd>
    </div>
  )
}

/** Units, as the model gave them: whole numbers whole, anything else to one decimal. */
function formatStock(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 1 })
}

/**
 * The model's numbers for the category. A category with no delivery on record
 * has no estimate and no confidence; each row then says so in words instead of
 * leaving a blank.
 */
function SupplyFacts({ supply }: { supply: PropertyCalendarSupply }) {
  const { last_delivery: last, stock_estimate: stock, runout_date: runout } = supply
  const hasHistory = last !== null

  return (
    <section aria-label="Estimate" className="space-y-1.5">
      <SectionHeading>Estimate</SectionHeading>
      {!hasHistory && (
        <p className="text-sm text-textMuted" data-testid="supply-sheet-no-history">
          No delivery on record for this category yet, so there is nothing to estimate from.
        </p>
      )}
      <dl className="divide-y divide-border">
        <Fact label="Last delivery">
          {last ? (
            <DeliveryLine delivery={last} />
          ) : (
            <span className="text-textMuted">None on record</span>
          )}
        </Fact>
        <Fact label="Stock estimate">
          {stock === null ? (
            <span className="text-textMuted">No estimate yet</span>
          ) : (
            `${formatStock(stock)} left`
          )}
        </Fact>
        <Fact label="Expected to run out">
          {runout ? (
            <span className={supply.runout_in_range ? 'text-amber' : undefined}>
              {formatSupplyDay(runout, { withYear: true })}
            </span>
          ) : (
            <span className="text-textMuted">No date yet</span>
          )}
        </Fact>
        <Fact label="Confidence">
          <Badge variant={CONFIDENCE_TONES[supply.confidence]}>
            {CONFIDENCE_LABELS[supply.confidence]}
          </Badge>
        </Fact>
      </dl>
    </section>
  )
}

function DeliveryLine({ delivery }: { delivery: PropertyCalendarSupplyDelivery }) {
  const detail = describeDelivery(delivery.qty, delivery.retailer)
  return (
    <>
      <span className="tabular-nums">{formatSupplyDay(delivery.date, { withYear: true })}</span>
      {detail && <span className="block text-xs text-textMuted">{detail}</span>}
    </>
  )
}

function DeliveriesThisMonth({ deliveries }: { deliveries: PropertyCalendarSupplyDelivery[] }) {
  const words = useRangeWords()
  if (deliveries.length === 0) return null
  return (
    <section aria-label={words.deliveriesHeading} className="space-y-1.5">
      <SectionHeading>{words.deliveriesHeading}</SectionHeading>
      <ul className="divide-y divide-border">
        {deliveries.map((delivery, index) => (
          <li
            key={`${delivery.date}:${index}`}
            className="py-2 text-sm text-text"
            data-testid="supply-sheet-delivery"
          >
            <DeliveryLine delivery={delivery} />
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * "What else to order": the supplies page's restock read, asked with this
 * category as already in the cart. Read-only; fetched when asked for, and
 * sharing the page's cache key. The button opens and closes the answer.
 */
function RestockSection({ propertyId, category }: { propertyId: string; category: string }) {
  const [asked, setAsked] = useState(false)
  const restock = useQuery({
    queryKey: ['supplies', 'restock', propertyId, [category]],
    queryFn: () => api.getRestockSuggestions(propertyId, [category]),
    enabled: asked,
    staleTime: 60_000,
  })

  return (
    <section aria-label="Ordering" className="space-y-2">
      <SectionHeading>Ordering</SectionHeading>
      <Button
        type="button"
        variant="outline"
        size="sm"
        aria-expanded={asked}
        onClick={() => setAsked(value => !value)}
      >
        <ShoppingCart className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
        What else to order
      </Button>
      {asked && (
        <div aria-live="polite" data-testid="supply-sheet-restock" className="space-y-1.5">
          {restock.isPending ? (
            <p className="text-sm text-textFaint">Checking…</p>
          ) : restock.isError ? (
            <p className="text-sm text-danger">
              Could not load suggestions.{' '}
              <button
                type="button"
                className="underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint"
                onClick={() => void restock.refetch()}
              >
                Retry
              </button>
            </p>
          ) : (
            <>
              <p className="text-sm text-textMuted">
                {restock.data.suggestions.length === 0
                  ? `Nothing else is due within ${restock.data.horizon_days} days of bookings.`
                  : `If you order ${categoryLabel(category).toLowerCase()} now, also add:`}
              </p>
              <ul className="space-y-1.5">
                {restock.data.suggestions.map(suggestion => (
                  <li key={suggestion.category} className="text-sm text-text">
                    <span className="font-medium">{categoryLabel(suggestion.category)}</span>
                    <span className="text-textMuted"> - {suggestion.reasoning}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </section>
  )
}
